import { memo, useState, useCallback, type VFC } from 'react'
import { NavItem } from '@panwds/react-nav'
import { PenIcon, CloseIcon, StarIcon } from '@panwds/icons'
import { classNames } from '@panwds/tailwindcss'
import { Button, Tooltip } from '@panwds/react-ui'
import NavIcon from '../common/NavIcon'
import FavoriteInput from './FavoriteInput'
import { addFavoritedNavItem, removeFavoritedNavItem } from '../../framework/actions'
import history from '../../framework/history'
import { _T } from '../../i18n'
import NoFavoritesPlaceholder from '../common/NoFavoritesPlaceholder'
import './builtInActions.scss'
import './FavoriteAction.scss'
import type { FavoritePreference, Preferences } from '../../framework/stateTypes'

const FavoriteListComp: VFC<{
  hidden: boolean
  preferences: Preferences
}> = ({ hidden, preferences }) => {
  const favorites = preferences?.tsgs?.current?.favorites || []
  const [editFavorite, setEditFavorite] = useState<FavoritePreference>()

  const onEditButtonClick = useCallback((fav: FavoritePreference) => {
    setEditFavorite(fav)
  }, [])

  const onDeleteButtonClick = useCallback((fav: FavoritePreference) => {
    removeFavoritedNavItem(fav.path)
  }, [])

  const onEditComplete = useCallback((favItem: FavoritePreference, newName: string | undefined) => {
    if (editFavorite?.path === favItem?.path) {
      setEditFavorite(undefined)
      if (newName && favItem.name !== newName) {
        addFavoritedNavItem({ ...favItem, name: newName })
      }
    }
  }, [editFavorite])

  const onFavClick = useCallback((fav: FavoritePreference) => {
    if (fav.path) {
      history.push(fav.path)
    }
  }, [])

  const getActionButtons = (fav: FavoritePreference) => {
    const buttonClass = classNames('hover:!twsf-text-[#E4E8EB]')
    return <div className='twsf-hidden favorite-action-buttons '>
      <Tooltip label={_T('Rename')} defaultPlacement='top'>
        <Button aria-label="Icon only button" addClassName='buttonClass twsf-ml-1' icon={<PenIcon size='xs' />} appearance="tertiary-clear" onClick={() => onEditButtonClick(fav)} />
      </Tooltip>
      <Tooltip label={_T('Remove from Favorites')} defaultPlacement='top'>
        <Button aria-label="Icon only button" addClassName={buttonClass} icon={<CloseIcon size='xs' />} appearance="tertiary-clear" onClick={() => onDeleteButtonClick(fav)} />
      </Tooltip>
    </div>
  }

  if (hidden) {
    return null
  }

  const classes = 'twsf-flex twsf-text-xs favorite-action-container twsf-flex twsf-pl-[16px] hover:twsf-bg-blue-steel-800 dark:hover:twsf-bg-gray-800 hover:twsf-text-white dark:hover:twsf-text-white'

  return <NavItem
    key='favorites'
    id='favorites'
    title={_T('Favorites')}
    displayArrow={false}
    icon={<NavIcon icon={StarIcon} />}
  >
    {favorites.length ? favorites.map((fav, idx) => {
      return editFavorite?.path === fav?.path ?
        <FavoriteInput key={`${fav.key}-${idx}`} favoriteItem={fav} onEditComplete={onEditComplete} /> :
        <div key={`${fav.key}-${idx}`} className={classes}>
          <Tooltip label={fav.tooltip} defaultPlacement='left-start'>
            <div className='twsf-flex-1 twsf-mt-2 twsf-cursor-pointer hover:twsf-mb-2' onClick={() => onFavClick(fav)}>
              {fav.name}
            </div>
          </Tooltip>
          {getActionButtons(fav)}
        </div>
    }) : <NoFavoritesPlaceholder />}
  </NavItem>
}

export const FavoriteList = memo(FavoriteListComp)
